'use client';

import GoodList from '@/app/(activity)/spring-sale/components/GoodList';
import { usePageCache } from '@/hooks/usePageCache';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { env } from '@/utils/env';
import { EPlatform } from '@/utils/enums/platform-enum';
import { Box } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useTranslations } from '@/i18n/use-translations';
import CampaignBanner, { CampaignBannerCarousel } from '../../components/CampaignBanner';
import { getCampaignFollowingAdExposureIndex } from '../../hooks/campaignTrackingProperties';
import { useCampaignPageTracking } from '../../hooks/useCampaignPageTracking';
import { MarketingMainModuleType } from '../../types';
import type {
  CampaignsServerData,
  MarketingMainCommodityVersion,
  MarketingMainResolvedModule,
} from '../../types';
import PromotionNavigation from './components/PromotionNavigation';
import {
  MarketingBrandProductsSection,
  MarketingCouponStrip,
  MarketingFlashSaleSection,
  MarketingRankingSection,
  MarketingSceneProductsSection,
  hasMarketingCouponList,
} from './components/MarketingAnniversaryBlocks';
import { getMarketingMainPageCacheKey } from './pageCache';

const DEFAULT_MARKETING_MAIN_BACKGROUND_COLOR = '#F4F4F4';
const MARKETING_BANNER_BOTTOM_DECORATION_SIGNATURE_MODULES = [
  MarketingMainModuleType.FLASH_SALE,
  MarketingMainModuleType.BRAND_PRODUCTS,
  MarketingMainModuleType.SCENE_PRODUCTS,
  MarketingMainModuleType.RANKING,
] as const;

interface MarketingPageProps {
  serverData: CampaignsServerData;
  isAppCommunicationReady: boolean;
  inAppH5: boolean;
  onBottomContentReady?: () => void;
}

interface MarketingNavigationProductsCacheState {
  selectedNavIndex?: number;
  expandedGroupIds?: Record<string, boolean>;
}

function shouldShowMarketingBannerBottomDecoration(serverData: CampaignsServerData) {
  const marketingData = serverData.runtimeConfig.marketing_data;

  if (
    !marketingData ||
    Number(marketingData.schema_version) !== 2 ||
    !Array.isArray(marketingData.modules)
  ) {
    return false;
  }

  const moduleTypes = new Set(marketingData.modules.map((module) => module.type));
  return MARKETING_BANNER_BOTTOM_DECORATION_SIGNATURE_MODULES.some((moduleType) =>
    moduleTypes.has(moduleType),
  );
}

function withS3Url(path?: string): string {
  const normalizedPath = typeof path === 'string' ? path.trim() : '';
  if (!normalizedPath) {
    return '';
  }
  if (/^(https?:)?\/\//.test(normalizedPath)) {
    return normalizedPath;
  }
  return `${env.NEXT_PUBLIC_S3_URL}${normalizedPath}`;
}

function MarketingBannerBottomDecoration({
  isWap,
  className,
}: {
  isWap: boolean;
  className: string;
}) {
  if (isWap) {
    return (
      <svg
        aria-hidden="true"
        focusable="false"
        className={className}
        viewBox="0 0 375 40"
        preserveAspectRatio="none"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M0 0C18.0865 20.4367 95.3847 35.7969 188 35.7969C279.001 35.7969 355.214 20.9669 375 1.06348V78H0V0Z"
          fill="#C7FF57"
        />
        <path
          d="M0 20.8108C37.8927 31.4338 107.901 38.5647 188 38.5647C267.399 38.5647 336.884 31.5576 375 21.0881V41.5061H0V20.8108Z"
          fill="#463AE8"
        />
      </svg>
    );
  }

  return (
    <svg
      aria-hidden="true"
      focusable="false"
      className={className}
      viewBox="0 0 1920 100"
      preserveAspectRatio="none"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M0 12C92.5342 57.2385 488.329 91.2418 962.56 91.2418C1428.51 91.2418 1818.75 58.4151 1920 14.3583V98H0V12Z"
        fill="#C7FF57"
      />
      <path
        d="M0 33.7805C194.138 71.5442 552.538 96.8831 962.561 96.8831C1369 96.883 1724.72 71.9831 1920 34.7649V198H0V33.7805Z"
        fill="#F4F4F4"
      />
    </svg>
  );
}

export default function MarketingPage({
  serverData,
  isAppCommunicationReady,
  inAppH5,
  onBottomContentReady,
}: MarketingPageProps) {
  const marketingMainData = serverData.marketingMainData;
  const pageData = marketingMainData?.pageData;
  const deviceType = serverData.deviceType as EPlatform;
  const isWap = deviceType !== EPlatform.WEB;
  const tMarketing = useTranslations('activity.campaigns.marketing');
  const setIsShowDtBanner = useStore((state) => state.setIsShowDtBanner);
  const isShowDtBanner = useStore((state) => state.isShowDtBanner);
  const [selectedNavIndex, setSelectedNavIndex] = useState(0);
  const commodityVersionList = useMemo<MarketingMainCommodityVersion[]>(
    () => marketingMainData?.commodityVersionList || pageData?.commodity_version || [],
    [marketingMainData?.commodityVersionList, pageData?.commodity_version],
  );

  const sectionRefs = useRef<Array<HTMLDivElement | null>>([]);
  const isScrolling = useRef(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastScrollTop = useRef(0);
  const scrollEndCheckRef = useRef<NodeJS.Timeout | null>(null);
  const pendingScrollIndexRef = useRef<number | null>(null);
  const topBannerExposureIndexesRef = useRef<Set<number>>(new Set());
  const [activeTopBannerIndex, setActiveTopBannerIndex] = useState(0);

  const {
    trackPageView,
    trackPageLeave,
    trackNavTabClick,
    trackViewMoreClick,
    trackProductListPitsClick,
    trackProductListPitsExposure,
    trackAdExp,
    trackTopBannerAdExposure,
    trackTopBannerAdClick,
    trackCouponMktExposure,
    trackCouponMktClick,
    trackButtonClick,
    trackCollectButtonClick,
  } = useCampaignPageTracking(serverData.runtimeConfig);

  const { ref: topBannerExposureRef, inView: topBannerInView } = useInView({
    threshold: 0.5,
  });

  const actualBannerData = useMemo(() => {
    if (isWap) {
      return marketingMainData?.bannerData || pageData?.wap_banner || pageData?.banner;
    }
    return marketingMainData?.bannerData || pageData?.banner;
  }, [isWap, marketingMainData?.bannerData, pageData?.banner, pageData?.wap_banner]);

  const actualBannerList = useMemo(() => {
    const bannerList = marketingMainData?.bannerList || pageData?.banner_list || [];
    const validBannerList = bannerList.filter((item) => item?.image);
    return validBannerList.length ? validBannerList : actualBannerData ? [actualBannerData] : [];
  }, [actualBannerData, marketingMainData?.bannerList, pageData?.banner_list]);

  const actualCommodityVersionList = useMemo(
    () =>
      commodityVersionList.map((item) => {
        const pcImage = withS3Url(item.image);
        const wapImage = withS3Url(item.wap_image || item.image);
        return {
          ...item,
          // PC 端兜底：未配置商品版位图片时回退到 wap_image
          image: pcImage || wapImage,
          wap_image: wapImage,
        };
      }),
    [commodityVersionList],
  );

  const actualNavigationData = useMemo(
    () =>
      (marketingMainData?.navigationData || pageData?.navigate || []).map((item) => {
        const pcImage = withS3Url(item.image);
        const wapImage = withS3Url(item.wap_image || item.image);
        return {
          ...item,
          // PC 端兜底：未配置导航选项图片时回退到 wap_image
          image: pcImage || wapImage,
          wap_image: wapImage,
        };
      }),
    [marketingMainData?.navigationData, pageData?.navigate],
  );
  const shouldShowNavigation =
    actualCommodityVersionList.length > 1 && actualNavigationData.length > 1;
  const marketingPageId = Number(serverData.runtimeConfig.marketing_page_id || 0);
  const couponBlock = serverData.runtimeConfig.marketing_data?.coupon_block;
  const couponConfig = marketingMainData?.couponConfig || serverData.runtimeConfig.coupon_config;
  const flashSaleBlock = serverData.runtimeConfig.marketing_data?.flash_sale_block;
  const brandProductsBlock = serverData.runtimeConfig.marketing_data?.brand_products;
  const sceneProductsBlock = serverData.runtimeConfig.marketing_data?.scene_products;
  const rankingBlock = serverData.runtimeConfig.marketing_data?.ranking_block;
  const { pageState, updatePageState } = usePageCache({
    pageKey: getMarketingMainPageCacheKey({
      slug: serverData.runtimeConfig.slug,
      marketingPageId,
      isPreview: serverData.isPreview,
      adp: serverData.adp,
    }),
    initialData: {},
  });
  const navigationProductsCache = pageState.navigationProducts as
    | MarketingNavigationProductsCacheState
    | undefined;
  const navigationProductsCacheRef = useRef<MarketingNavigationProductsCacheState>({});
  const updateNavigationProductsCache = useCallback(
    (nextState: MarketingNavigationProductsCacheState) => {
      const nextNavigationProductsCache = {
        ...navigationProductsCacheRef.current,
        ...nextState,
      };
      navigationProductsCacheRef.current = nextNavigationProductsCache;
      updatePageState({
        navigationProducts: nextNavigationProductsCache,
      });
    },
    [updatePageState],
  );
  const hasCouponBlock = Boolean(couponBlock);
  const hasWapCouponModule = isWap && !hasCouponBlock && hasMarketingCouponList(couponConfig);
  const contentClassName =
    !shouldShowNavigation && (actualBannerList.length > 0 || hasWapCouponModule) ? 'pt-[10px]' : '';
  const pageStyle = serverData.runtimeConfig.marketing_data?.page_style;
  const pageBackgroundColor =
    (isWap ? pageStyle?.wap_background_color : pageStyle?.pc_background_color) ||
    DEFAULT_MARKETING_MAIN_BACKGROUND_COLOR;
  const shouldShowBannerBottomDecoration = shouldShowMarketingBannerBottomDecoration(serverData);
  const bannerBottomDecoration = isWap
    ? {
        className: 'h-[40px]',
      }
    : {
        className: 'h-[100px]',
      };

  useEffect(() => {
    navigationProductsCacheRef.current = navigationProductsCache || {};
  }, [navigationProductsCache]);

  useEffect(() => {
    const cachedIndex = Number(navigationProductsCache?.selectedNavIndex);

    if (
      !Number.isFinite(cachedIndex) ||
      cachedIndex < 0 ||
      cachedIndex >= actualCommodityVersionList.length
    ) {
      return;
    }

    setSelectedNavIndex((current) => (current === cachedIndex ? current : cachedIndex));
  }, [actualCommodityVersionList.length, navigationProductsCache?.selectedNavIndex]);

  const renderModules = useMemo<MarketingMainResolvedModule[]>(() => {
    const configuredModules = marketingMainData?.modules || [];
    const baseModules =
      configuredModules.length > 0
        ? configuredModules
        : ([
            actualBannerList.length > 0
              ? {
                  id: 'marketing-main-banner',
                  type: MarketingMainModuleType.BANNER,
                  sort: 10,
                  banner: actualBannerList[0] || null,
                  banners: actualBannerList,
                }
              : null,
            hasMarketingCouponList(couponConfig)
              ? {
                  id: 'marketing-main-coupon',
                  type: MarketingMainModuleType.COUPON,
                  sort: couponBlock?.sort ?? 20,
                  couponConfig,
                  couponBlock,
                }
              : null,
            flashSaleBlock?.visible
              ? {
                  id: 'marketing-main-flash-sale',
                  type: MarketingMainModuleType.FLASH_SALE,
                  sort: flashSaleBlock.sort,
                  flashSaleBlock,
                }
              : null,
            brandProductsBlock?.visible
              ? {
                  id: 'marketing-main-brand-products',
                  type: MarketingMainModuleType.BRAND_PRODUCTS,
                  sort: brandProductsBlock.sort,
                  brandProductsBlock,
                }
              : null,
            sceneProductsBlock?.visible
              ? {
                  id: 'marketing-main-scene-products',
                  type: MarketingMainModuleType.SCENE_PRODUCTS,
                  sort: sceneProductsBlock.sort,
                  sceneProductsBlock,
                }
              : null,
            rankingBlock?.visible
              ? {
                  id: 'marketing-main-ranking',
                  type: MarketingMainModuleType.RANKING,
                  sort: rankingBlock.sort,
                  rankingBlock,
                }
              : null,
            actualCommodityVersionList.length > 0
              ? {
                  id: 'marketing-main-navigation-products',
                  type: MarketingMainModuleType.NAVIGATION_PRODUCTS,
                  sort: 26,
                  navigationData: actualNavigationData,
                  commodityVersionList: actualCommodityVersionList,
                }
              : null,
          ].filter(Boolean) as MarketingMainResolvedModule[]);

    return baseModules
      .map((module) => {
        switch (module.type) {
          case MarketingMainModuleType.BANNER:
            return {
              ...module,
              banner: actualBannerData || module.banner,
              banners: actualBannerList.length ? actualBannerList : module.banners,
            };
          case MarketingMainModuleType.COUPON:
            return {
              ...module,
              couponConfig: module.couponConfig || couponConfig,
              couponBlock: module.couponBlock || couponBlock,
            };
          case MarketingMainModuleType.FLASH_SALE:
            return {
              ...module,
              flashSaleBlock: module.flashSaleBlock || flashSaleBlock,
            };
          case MarketingMainModuleType.BRAND_PRODUCTS:
            return {
              ...module,
              brandProductsBlock: module.brandProductsBlock || brandProductsBlock,
            };
          case MarketingMainModuleType.SCENE_PRODUCTS:
            return {
              ...module,
              sceneProductsBlock: module.sceneProductsBlock || sceneProductsBlock,
            };
          case MarketingMainModuleType.RANKING:
            return {
              ...module,
              rankingBlock: module.rankingBlock || rankingBlock,
            };
          case MarketingMainModuleType.NAVIGATION_PRODUCTS:
            return {
              ...module,
              navigationData: actualNavigationData,
              commodityVersionList: actualCommodityVersionList,
            };
          default:
            return module;
        }
      })
      .sort((a, b) => a.sort - b.sort);
  }, [
    actualBannerData,
    actualBannerList,
    actualCommodityVersionList,
    actualNavigationData,
    couponBlock,
    flashSaleBlock,
    brandProductsBlock,
    sceneProductsBlock,
    rankingBlock,
    marketingMainData?.modules,
    couponConfig,
  ]);

  useEffect(() => {
    if (isAppCommunicationReady) {
      onBottomContentReady?.();
    }
  }, [isAppCommunicationReady, onBottomContentReady]);

  useEffect(() => {
    if (!isAppCommunicationReady) return;

    trackPageView();

    if (inAppH5) {
      window.addEventListener('appView', trackPageView);
      window.addEventListener('appLeave', trackPageLeave);
    }

    return () => {
      trackPageLeave();
      if (inAppH5) {
        window.removeEventListener('appView', trackPageView);
        window.removeEventListener('appLeave', trackPageLeave);
      }
    };
  }, [inAppH5, isAppCommunicationReady, trackPageLeave, trackPageView]);

  useEffect(() => {
    topBannerExposureIndexesRef.current.clear();
    setActiveTopBannerIndex(0);
  }, [actualBannerList]);

  useEffect(() => {
    const topBanner =
      actualBannerList[activeTopBannerIndex] || actualBannerList[0] || actualBannerData;
    const pitsIndex = actualBannerList[activeTopBannerIndex] ? activeTopBannerIndex : 0;

    if (!topBannerInView || !topBanner?.image) return;
    if (topBannerExposureIndexesRef.current.has(pitsIndex)) return;

    topBannerExposureIndexesRef.current.add(pitsIndex);
    trackTopBannerAdExposure(topBanner.jump_content, pitsIndex);
  }, [
    activeTopBannerIndex,
    actualBannerList,
    actualBannerData,
    topBannerInView,
    trackTopBannerAdExposure,
  ]);

  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      if (scrollEndCheckRef.current) {
        clearTimeout(scrollEndCheckRef.current);
      }
    };
  }, []);

  const checkScrollEnd = useCallback(() => {
    const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;

    if (Math.abs(currentScrollTop - lastScrollTop.current) < 1) {
      isScrolling.current = false;
      if (scrollEndCheckRef.current) {
        clearTimeout(scrollEndCheckRef.current);
        scrollEndCheckRef.current = null;
      }
      return;
    }

    lastScrollTop.current = currentScrollTop;
    if (scrollEndCheckRef.current) {
      clearTimeout(scrollEndCheckRef.current);
    }
    scrollEndCheckRef.current = setTimeout(checkScrollEnd, 150);
  }, []);

  const handleScroll = useCallback(() => {
    if (isScrolling.current) {
      lastScrollTop.current = window.pageYOffset || document.documentElement.scrollTop;

      if (scrollEndCheckRef.current) {
        clearTimeout(scrollEndCheckRef.current);
      }
      scrollEndCheckRef.current = setTimeout(checkScrollEnd, 150);
      return;
    }

    const viewportCenter = window.innerHeight / 2;
    let currentIndex = 0;
    let minDistance = Infinity;

    sectionRefs.current.forEach((ref, index) => {
      if (!ref) return;

      const rect = ref.getBoundingClientRect();
      const sectionTop = rect.top;
      const sectionBottom = rect.bottom;
      const distance =
        sectionTop <= viewportCenter && sectionBottom >= viewportCenter
          ? Math.min(
              Math.abs(sectionTop - viewportCenter),
              Math.abs(sectionBottom - viewportCenter),
            ) * 0.1
          : sectionBottom < viewportCenter
            ? viewportCenter - sectionBottom
            : sectionTop - viewportCenter;

      if (distance < minDistance) {
        minDistance = distance;
        currentIndex = index;
      }
    });

    if (currentIndex !== selectedNavIndex) {
      setSelectedNavIndex(currentIndex);
      updateNavigationProductsCache({
        selectedNavIndex: currentIndex,
      });
    }
  }, [checkScrollEnd, selectedNavIndex, updateNavigationProductsCache]);

  const executeScroll = useCallback(
    (index: number) => {
      const targetRef = sectionRefs.current[index];
      const isApp = [EPlatform.ANDROID, EPlatform.IOS].includes(deviceType);
      if (!targetRef) return;

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      if (scrollEndCheckRef.current) {
        clearTimeout(scrollEndCheckRef.current);
      }

      isScrolling.current = true;
      lastScrollTop.current = window.pageYOffset || document.documentElement.scrollTop;

      const headerHeight = isApp
        ? 0
        : parseInt(
            getComputedStyle(document.documentElement).getPropertyValue('--global-header-height') ||
              '100',
            10,
          );
      const productNavElement = document.querySelector('.product-navigation');
      const navHeight = productNavElement ? productNavElement.getBoundingClientRect().height : 120;
      let offset = headerHeight + navHeight;

      if (isWap && !isApp) {
        offset -= (50 / 375) * window.innerWidth;
      }

      window.scrollTo({
        top: targetRef.offsetTop - offset,
        behavior: 'smooth',
      });

      scrollEndCheckRef.current = setTimeout(checkScrollEnd, 150);
      scrollTimeoutRef.current = setTimeout(() => {
        isScrolling.current = false;
        if (scrollEndCheckRef.current) {
          clearTimeout(scrollEndCheckRef.current);
          scrollEndCheckRef.current = null;
        }
      }, 2000);
    },
    [checkScrollEnd, deviceType, isWap],
  );

  const scrollToSection = useCallback(
    (index: number) => {
      if (isShowDtBanner) {
        setIsShowDtBanner(false);
        pendingScrollIndexRef.current = index;
        return;
      }

      executeScroll(index);
    },
    [executeScroll, isShowDtBanner, setIsShowDtBanner],
  );

  useEffect(() => {
    if (!isShowDtBanner && pendingScrollIndexRef.current !== null) {
      const index = pendingScrollIndexRef.current;
      pendingScrollIndexRef.current = null;
      setTimeout(() => {
        executeScroll(index);
      }, 300);
    }
  }, [executeScroll, isShowDtBanner]);

  useEffect(() => {
    let ticking = false;

    const throttledHandleScroll = () => {
      if (ticking) return;
      requestAnimationFrame(() => {
        handleScroll();
        ticking = false;
      });
      ticking = true;
    };

    window.addEventListener('scroll', throttledHandleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', throttledHandleScroll);
    };
  }, [handleScroll]);

  const transformProductData = useCallback((product: any) => {
    return {
      id: product.id,
      name: product.name,
      short_name: product.short_name,
      shop_sku: product.shop_sku,
      business_sku: product.business_sku,
      image: product.image,
      price_info: product.price_info,
      is_sold_out: product.stock === 0,
      ...product,
    };
  }, []);

  const handleViewMoreCallback = useCallback(() => {
    requestAnimationFrame(() => {
      setTimeout(() => {
        handleScroll();
      }, 100);
    });
  }, [handleScroll]);

  if (!pageData || !isAppCommunicationReady) {
    return null;
  }

  return (
    <Box className="w-full" style={{ backgroundColor: pageBackgroundColor }}>
      {renderModules.map((module) => {
        switch (module.type) {
          case MarketingMainModuleType.BANNER: {
            const firstBanner = module.banners[0];

            return firstBanner ? (
              <div key={module.id} ref={topBannerExposureRef} className="relative">
                {module.banners.length > 1 ? (
                  <CampaignBannerCarousel
                    data={module.banners}
                    autoplayDelay={isWap ? 3000 : 5000}
                    isMobile={isWap}
                    pauseOnMouseEnter={!isWap}
                    onActiveIndexChange={setActiveTopBannerIndex}
                    onBannerClick={(banner, bannerIndex) =>
                      trackTopBannerAdClick(banner.jump_content, bannerIndex)
                    }
                  />
                ) : (
                  <CampaignBanner
                    data={{
                      image: firstBanner.image,
                      image_alt: firstBanner.image_alt,
                      type: firstBanner.type,
                      jump_content: firstBanner.jump_content,
                    }}
                    aspectRatio={firstBanner.aspectRatio}
                    onBannerClick={(banner, bannerIndex) =>
                      trackTopBannerAdClick(banner.jump_content, bannerIndex)
                    }
                  />
                )}
                {shouldShowBannerBottomDecoration && (
                  <MarketingBannerBottomDecoration
                    isWap={isWap}
                    className={`pointer-events-none absolute bottom-0 left-0 right-0 z-10 block w-full select-none ${bannerBottomDecoration.className}`}
                  />
                )}
                {!isWap && !hasCouponBlock && (
                  <MarketingCouponStrip
                    couponConfig={couponConfig}
                    marketingPageId={marketingPageId}
                    isPreview={serverData.isPreview}
                    isWap={isWap}
                    inAppH5={inAppH5}
                    placement="banner"
                    onCouponExposure={trackCouponMktExposure}
                  />
                )}
              </div>
            ) : null;
          }

          case MarketingMainModuleType.COUPON:
            return (
              <MarketingCouponStrip
                key={module.id}
                couponConfig={module.couponConfig}
                couponBlock={module.couponBlock}
                marketingPageId={marketingPageId}
                isPreview={serverData.isPreview}
                isWap={isWap}
                isWebPage={!isWap}
                inAppH5={inAppH5}
                placement={module.couponBlock ? 'block' : 'flow'}
                couponExposureModuleName={module.couponBlock ? '优惠券位' : undefined}
                onCouponExposure={trackCouponMktExposure}
                onCouponClick={module.couponBlock ? trackCouponMktClick : undefined}
                onButtonClick={module.couponBlock ? trackButtonClick : undefined}
              />
            );

          case MarketingMainModuleType.FLASH_SALE:
            return (
              <MarketingFlashSaleSection
                key={module.id}
                flashSaleBlock={module.flashSaleBlock}
                marketingPageId={marketingPageId}
                isPreview={serverData.isPreview}
                adp={serverData.adp}
                isWap={isWap}
                onProductExposure={(product, productIndex) => {
                  trackProductListPitsExposure(product, productIndex, '秒杀版位', 'Flash Sale');
                }}
                onProductClick={(product, productIndex) => {
                  trackProductListPitsClick(product, productIndex, '秒杀版位', 'Flash Sale');
                }}
              />
            );

          case MarketingMainModuleType.BRAND_PRODUCTS:
            return (
              <MarketingBrandProductsSection
                key={module.id}
                brandProductsBlock={module.brandProductsBlock}
                marketingPageId={marketingPageId}
                isPreview={serverData.isPreview}
                isWap={isWap}
                cacheState={pageState.brandProducts}
                onCacheStateChange={(brandProducts) => {
                  updatePageState({ brandProducts });
                }}
                onCollectClick={(product, productIndex, collectType) => {
                  trackCollectButtonClick(product, productIndex, '品牌商品版位', collectType);
                }}
                onProductExposure={(product, productIndex) => {
                  trackProductListPitsExposure(
                    product,
                    productIndex,
                    '品牌商品版位',
                    'Brand Anniversary Picks',
                  );
                }}
                onProductClick={(product, productIndex) => {
                  trackProductListPitsClick(
                    product,
                    productIndex,
                    '品牌商品版位',
                    'Brand Anniversary Picks',
                  );
                }}
                onViewMoreClick={() => {
                  trackViewMoreClick('Brand Anniversary Picks', '品牌商品版位');
                }}
              />
            );

          case MarketingMainModuleType.SCENE_PRODUCTS:
            return (
              <MarketingSceneProductsSection
                key={module.id}
                sceneProductsBlock={module.sceneProductsBlock}
                marketingPageId={marketingPageId}
                isPreview={serverData.isPreview}
                adp={serverData.adp}
                isWap={isWap}
                onProductExposure={(product, productIndex, tabName) => {
                  trackProductListPitsExposure(product, productIndex, '场景商品版位', tabName);
                }}
                onProductClick={(product, productIndex, tabName) => {
                  trackProductListPitsClick(product, productIndex, '场景商品版位', tabName);
                }}
                onTabClick={(tabName, tabIndex) => {
                  trackNavTabClick(tabName, tabIndex, '场景商品版位');
                }}
              />
            );

          case MarketingMainModuleType.RANKING:
            return (
              <MarketingRankingSection
                key={module.id}
                rankingBlock={module.rankingBlock}
                isWap={isWap}
                cacheState={pageState.ranking}
                onCacheStateChange={(ranking) => {
                  updatePageState({ ranking });
                }}
                onTabClick={(tabName, tabIndex) => {
                  trackNavTabClick(tabName, tabIndex, '榜单版位');
                }}
                onViewMoreClick={(tabName) => {
                  trackViewMoreClick(tabName, '榜单版位');
                }}
                onProductExposure={(product, productIndex, tabName) => {
                  trackProductListPitsExposure(product, productIndex, '榜单版位', tabName);
                }}
                onProductClick={(product, productIndex, tabName) => {
                  trackProductListPitsClick(product, productIndex, '榜单版位', tabName);
                }}
              />
            );

          case MarketingMainModuleType.NAVIGATION_PRODUCTS:
            return (
              <div key={module.id} className={contentClassName}>
                {shouldShowNavigation && (
                  <PromotionNavigation
                    navigationData={module.navigationData}
                    selectedIndex={selectedNavIndex}
                    isSticky
                    disableAutoSelect={isScrolling.current}
                    onNavigationClick={(item, index) => {
                      logger.log(`营销主活动页导航项被点击: ${item.title}, 索引: ${index}`);
                      trackNavTabClick(item.title, index);
                      setSelectedNavIndex(index);
                      updateNavigationProductsCache({
                        selectedNavIndex: index,
                      });
                      scrollToSection(index);
                    }}
                  />
                )}

                {module.commodityVersionList.map((commodityGroup, validIndex) => {
                  const groupId = commodityGroup.product_group_id.toString();
                  const isGroupExpanded = Boolean(
                    navigationProductsCache?.expandedGroupIds?.[groupId],
                  );

                  return (
                    <div
                      key={`marketing-main-group-${commodityGroup.product_group_id}-${validIndex}`}
                      ref={(el) => {
                        sectionRefs.current[validIndex] = el;
                      }}
                    >
                      <GoodList
                        index={validIndex}
                        tabName={module.navigationData?.[validIndex]?.title || 'unknown'}
                        promoImage={{
                          image: commodityGroup.image || commodityGroup.wap_image || '',
                          wap_image: commodityGroup.wap_image,
                          title: `商品组 ${commodityGroup.product_group_id}`,
                          word: `商品组 ${commodityGroup.product_group_id}`,
                          image_alt: commodityGroup.image_alt || '',
                          description: `商品组 ${commodityGroup.product_group_id} 描述`,
                          aspectRatio: '1920/580',
                          jump_type: 1,
                          jump_value: groupId,
                          image_title: commodityGroup.image_title,
                        }}
                        products={commodityGroup.product_list.map(transformProductData)}
                        deviceType={deviceType}
                        pcPromoStickyTop={shouldShowNavigation ? undefined : '100px'}
                        pcPromoImageHeight={
                          shouldShowNavigation ? undefined : 'calc(100vh - 112px)'
                        }
                        showAllProducts={isGroupExpanded}
                        onShowAllProductsChange={(showAllProducts) => {
                          updateNavigationProductsCache({
                            expandedGroupIds: {
                              ...(navigationProductsCacheRef.current.expandedGroupIds || {}),
                              [groupId]: showAllProducts,
                            },
                          });
                        }}
                        onProductClick={(product, productIndex) => {
                          trackProductListPitsClick(
                            product,
                            productIndex,
                            '商品版位',
                            module.navigationData?.[validIndex]?.title,
                          );
                        }}
                        onProductExp={(product, productIndex) => {
                          trackProductListPitsExposure(
                            product,
                            productIndex,
                            '商品版位',
                            module.navigationData?.[validIndex]?.title,
                          );
                        }}
                        onAdExp={(moduleName, tabName, index) => {
                          trackAdExp(
                            moduleName,
                            tabName,
                            getCampaignFollowingAdExposureIndex(index),
                          );
                        }}
                        viewMoreConfig={{
                          groupId,
                          text: tMarketing('common.viewMore'),
                          showButton: true,
                        }}
                        onViewMoreClick={() => {
                          trackViewMoreClick(
                            module.navigationData?.[validIndex]?.title || 'unknown',
                          );
                          handleViewMoreCallback();
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            );

          default:
            return null;
        }
      })}
    </Box>
  );
}
